import React, { useState, useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  ArrowLeft,
  BookOpen,
  ChevronRight,
  HelpCircle,
  Search,
  Printer,
} from 'lucide-react';
import Logo from '../components/Logo';

/**
 * In-app User Manual + FAQ.
 *
 * Audience filtering:
 *   - admin    -> sees ALL sections
 *   - creator  -> sees only sections where adminOnly !== true
 *
 * Each section / FAQ item has an explicit audience flag.  Nothing about
 * admin-only operations leaks into a creator's view.
 */

// ---------- Content definition --------------------------------------------

const MANUAL_SECTIONS = [
  {
    id: 'welcome',
    title: 'Welcome to TMC Surveys',
    audience: 'all',
    body: (role) => (
      <>
        <p>
          TMC Surveys is a self-hosted survey platform for collecting feedback,
          tracking trends, and acting on the results. This manual is tailored to
          your role — you are currently signed in as a{' '}
          <strong>{role === 'admin' ? 'Administrator' : 'Creator'}</strong>.
        </p>
        <p>
          Use the navigation on the left to jump to any topic. Sections marked
          with a key icon are role-restricted and are only visible to those who
          need them.
        </p>
      </>
    ),
  },
  {
    id: 'sign-in',
    title: 'Signing In',
    audience: 'all',
    body: () => (
      <>
        <p>You can sign in two ways:</p>
        <ol>
          <li>
            <strong>Sign in with Microsoft</strong> — the recommended path for
            internal staff. This uses your therapymgmt.com Microsoft account; no
            separate password is needed.
          </li>
          <li>
            <strong>Email &amp; password</strong> — a fallback for service
            accounts. Click <em>“Use email &amp; password instead”</em> on the
            login screen.
          </li>
        </ol>
        <p>
          After signing in, you land on your dashboard. If a colleague’s
          surveys are missing from your list, that is by design — see the “Who
          sees what” section below.
        </p>
      </>
    ),
  },
  {
    id: 'who-sees-what',
    title: 'Who sees what (your role)',
    audience: 'all',
    body: (role) => (
      <>
        <p>
          TMC Surveys uses two roles. Your role determines what surveys and
          settings you can see.
        </p>
        <table className="w-full text-left mt-3 mb-4 border border-[#E5E0D8] rounded-lg overflow-hidden">
          <thead className="bg-[#F4F1EA] text-sm text-[#5C635A]">
            <tr>
              <th className="px-4 py-2">Capability</th>
              <th className="px-4 py-2">Creator</th>
              <th className="px-4 py-2">Administrator</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">Create surveys</td><td className="px-4 py-2">✅</td><td className="px-4 py-2">✅</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">View / edit own surveys</td><td className="px-4 py-2">✅</td><td className="px-4 py-2">✅</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">View surveys shared via a group</td><td className="px-4 py-2">✅ (read &amp; reports only)</td><td className="px-4 py-2">✅</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">View other people’s surveys</td><td className="px-4 py-2">—</td><td className="px-4 py-2">✅</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">Create / manage groups</td><td className="px-4 py-2">—</td><td className="px-4 py-2">✅</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">Manage users</td><td className="px-4 py-2">—</td><td className="px-4 py-2">✅</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">Org-wide analytics</td><td className="px-4 py-2">—</td><td className="px-4 py-2">✅</td></tr>
          </tbody>
        </table>
        <p className="text-sm text-[#5C635A]">
          You are a <strong>{role === 'admin' ? 'Administrator' : 'Creator'}</strong>.
          {role === 'admin'
            ? ' You can see everything described in this manual.'
            : ' Sections that don’t apply to you have been hidden automatically.'}
        </p>
      </>
    ),
  },
  {
    id: 'creating-a-survey',
    title: 'Creating a survey',
    audience: 'all',
    body: () => (
      <>
        <ol>
          <li>From the dashboard, click <strong>Create Survey</strong> in the top right.</li>
          <li>Give it a <strong>name</strong> (shown to respondents) and a short <strong>description</strong>.</li>
          <li>Add questions one by one. Each question has a type:
            <ul>
              <li><strong>Multiple Choice (pick one)</strong> — radio buttons, exactly one option per respondent.</li>
              <li><strong>Select All That Apply</strong> — checkboxes, any number of options can be ticked.</li>
              <li><strong>Star Rating</strong> — 1–5 stars.</li>
              <li><strong>Short Answer</strong> / <strong>Long Answer</strong> — free-text.</li>
            </ul>
          </li>
          <li>Toggle <em>Required</em> if the question must be answered to submit. For "Select All That Apply", required means <em>at least one</em> option must be picked.</li>
          <li>
            <strong>Reorder questions any time</strong> — drag the <em>grip handle</em> at the
            left edge of a question card up or down, or click the up/down arrows above and
            below it. The new order saves with the rest of the survey.
          </li>
          <li>Click <strong>Save</strong>. Your new survey appears in the dashboard.</li>
        </ol>
        <p className="text-sm text-[#5C635A]">
          Tip: Keep surveys short. Star ratings + 1–2 short text follow-ups are usually enough for
          actionable feedback.
        </p>
      </>
    ),
  },
  {
    id: 'groups-sharing',
    title: 'Groups & sharing surveys',
    audience: 'all',
    body: (role) => (
      <>
        <p>
          By default, a survey is only visible to its <strong>creator</strong> (you) and{' '}
          <strong>administrators</strong>. To let teammates see a survey on their dashboard and
          run reports from it, tag the survey with one or more <strong>groups</strong>.
        </p>
        <ol>
          <li>
            An admin creates groups (e.g., <em>Branding</em>, <em>Operations</em>) and adds
            people to them.
          </li>
          <li>
            When you create or edit one of your surveys, open the{' '}
            <strong>Groups &amp; Sharing</strong> section and pick one or more groups from the
            dropdown.
          </li>
          <li>
            Everyone in those groups will now see the survey on their dashboard with a{' '}
            <span className="px-1.5 py-0.5 rounded-full bg-[#C67D63]/15 text-[#a96550] uppercase tracking-wide text-xs">Shared</span>{' '}
            badge and can view analytics + run reports from it.
          </li>
          <li>
            Remove a tag (or remove the group entirely) and the share is revoked instantly.
          </li>
        </ol>
        <p>
          <strong>Who can tag a survey?</strong> Only the <em>survey owner</em> or an{' '}
          <em>administrator</em>. Group members get <strong>read-only</strong> access — they can
          view, export, and run reports, but they can’t edit, retag, or delete a survey that
          isn’t theirs.
        </p>
        <p>
          <strong>Letting people know:</strong> when you tag a survey with a new group, tick the{' '}
          <em>“Email members of newly-added groups when I save”</em> checkbox in the editor. We
          send a one-time email to everyone in that group with a link to their dashboard. The
          email only goes out for groups you <em>just added</em> — re-saving won’t spam
          existing members.
        </p>
        {role === 'admin' ? (
          <p className="text-sm text-[#5C635A]">
            See the <strong>Managing groups</strong> section below for how to create, rename, and
            populate groups.
          </p>
        ) : (
          <p className="text-sm text-[#5C635A]">
            Need a new group? Ask an administrator — they can create one in seconds from the
            Groups tab.
          </p>
        )}
      </>
    ),
  },
  {
    id: 'branding',
    title: 'Branding & theme',
    audience: 'all',
    body: () => (
      <>
        <p>
          Each survey can have its own look. Open a survey in the editor and
          expand <strong>Branding &amp; Theme</strong>. You’ll find:
        </p>
        <ul>
          <li>
            <strong>Logo</strong> — PNG, JPG, SVG, or WebP up to 5 MB. It’s
            shown above the survey on a clean white card.
          </li>
          <li>
            <strong>Theme presets</strong> — seven curated combinations of color +
            font + background (Calm Sage, Ocean Mist, Sunset Warmth, Quiet Plum,
            Modern Mono, Warm Cocoa, and <strong>TMC Brand</strong> — the
            official navy + orange palette with Montserrat and the white TMC
            logo). One click swaps everything.
          </li>
          <li>
            <strong>Background style</strong> — solid, soft gradient, dot grid,
            line grid, gentle waves, organic “soft blobs”, or your own{' '}
            <strong>Custom Image</strong> (upload JPG/PNG/WebP up to 8 MB with
            an adjustable darkness overlay for legibility).
          </li>
          <li>
            <strong>Card style</strong> — flat, elevated white, frosted glass,
            or sharp bordered.
          </li>
          <li>
            <strong>Font</strong> — Outfit, Inter, Playfair Display, Space
            Grotesk, or Montserrat, each loaded from Google Fonts.
          </li>
          <li>
            <strong>Live preview</strong> updates instantly so you see exactly
            what respondents will see.
          </li>
          <li>
            <strong>Fine-tune colors manually</strong> — expandable advanced
            section if you want to override any color from a preset.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'sharing',
    title: 'Sharing — public link & QR code',
    audience: 'all',
    body: () => (
      <>
        <p>Every survey has a public URL. There are three ways to share it:</p>
        <ol>
          <li>
            <strong>Copy link</strong> — click the clipboard icon on the survey
            card; paste it into an email or text.
          </li>
          <li>
            <strong>QR code</strong> — click the QR icon to open a printable QR.
            Use <em>Download PNG</em> to drop it on a flyer, intake form, or
            waiting-room poster.
          </li>
          <li>
            <strong>Preview</strong> — click the external-link icon to open the
            public survey in a new tab; useful for testing.
          </li>
        </ol>
        <p className="text-sm text-[#5C635A]">
          Respondents do <strong>not</strong> need an account. The link is the only thing
          required to submit feedback.
        </p>
      </>
    ),
  },
  {
    id: 'analytics',
    title: 'Viewing analytics',
    audience: 'all',
    body: () => (
      <>
        <p>
          Click the chart icon on any survey card to open its analytics page. You’ll see:
        </p>
        <ul>
          <li>Total responses and completion rate</li>
          <li>Responses over the last 30 days (line chart)</li>
          <li>Per-question breakdowns — averages for star ratings, distributions for radio questions</li>
          <li>A paginated table of every individual response</li>
        </ul>
      </>
    ),
  },
  {
    id: 'exports',
    title: 'Exporting responses',
    audience: 'all',
    body: () => (
      <>
        <p>
          From a survey’s analytics page, you can download a single-survey
          export in three formats:
        </p>
        <ul>
          <li><strong>CSV</strong> — one row per response, raw values. Best for spreadsheets and stats tools.</li>
          <li><strong>Excel</strong> — same data with a styled header, opens cleanly in Excel.</li>
          <li><strong>PDF</strong> — printable summary with totals, completion rate, and per-question stats.</li>
        </ul>
      </>
    ),
  },
  {
    id: 'reports',
    title: 'Multi-survey reports',
    audience: 'all',
    body: (role) => (
      <>
        <p>
          The <strong>Reports</strong> tab on the dashboard lets you combine
          data from several surveys into one report.
        </p>
        <ol>
          <li>Tick the surveys you want to include. <em>Select all</em> and <em>Clear</em> buttons are at the top.</li>
          <li>(Optional) Restrict the date range — only responses submitted between those dates are included.</li>
          <li>Pick a format: <strong>PDF</strong> (printable summary), <strong>Excel</strong> (one sheet per survey + a Summary sheet), or <strong>CSV</strong> (long-format raw data, one row per question per response).</li>
          <li>(Optional) Toggle <em>Include AI-written executive summary</em> before generating a PDF.</li>
          <li>Click <strong>Generate report</strong>. Your browser downloads the file when it’s ready.</li>
        </ol>
        <p className="text-sm text-[#5C635A]">
          {role === 'admin'
            ? 'As an admin you can pick any combination of surveys across the whole organization.'
            : 'You can only include surveys you own. Surveys created by other people won’t appear in the list.'}
        </p>
      </>
    ),
  },
  {
    id: 'ai-insights',
    title: 'AI Insights — Claude-powered analysis',
    audience: 'all',
    body: () => (
      <>
        <p>
          On the Reports tab, the <strong>AI Insights</strong> panel sends your
          selected surveys to Anthropic’s <em>Claude Sonnet 4.5</em> and asks for
          a 3–5 paragraph executive summary. The summary covers:
        </p>
        <ul>
          <li>Overall sentiment and response volume</li>
          <li>Notable trends and outliers between surveys</li>
          <li>Common themes from open-ended (free-text) responses</li>
          <li>Two to three concrete, actionable recommendations</li>
          <li>A bottom-line takeaway</li>
        </ul>
        <p>Two ways to use it:</p>
        <ol>
          <li>
            <strong>On-screen panel</strong> — click <em>Analyse with AI</em>.
            The result appears inline; use <em>Copy</em> to paste it into an
            email, ticket, or doc.
          </li>
          <li>
            <strong>Embedded in a PDF</strong> — keep <em>Include AI-written
            executive summary</em> checked, pick PDF format, and the same
            summary appears on the second page of the downloaded report.
          </li>
        </ol>
        <p className="text-sm text-[#5C635A]">
          The AI receives aggregated stats and a sample of free-text answers
          only. It never sees submitter information (responses are already
          anonymous). If the data is thin, it will say so plainly instead of
          inventing trends.
        </p>
      </>
    ),
  },
  {
    id: 'reports-scheduled',
    title: 'Scheduled email reports',
    audience: 'all',
    body: () => (
      <>
        <p>
          You can have a summary report emailed automatically — useful for weekly
          leadership reviews. In the survey editor, expand{' '}
          <strong>Report schedule</strong> and:
        </p>
        <ol>
          <li>Toggle <em>Enabled</em>.</li>
          <li>Pick a frequency: daily, weekly, or monthly.</li>
          <li>Set the hour of day (server local time) and, for weekly/monthly, the day.</li>
          <li>Add one or more recipient emails.</li>
          <li>Save. The next scheduled run will deliver the report.</li>
        </ol>
        <p className="text-sm text-[#5C635A]">
          Reports include total responses and per-question averages/distributions
          for the period since the last send.
        </p>
      </>
    ),
  },
  {
    id: 'change-password',
    title: 'Account password',
    audience: 'all',
    body: () => (
      <>
        <p>
          TMC Surveys does not manage your password. Sign-in uses your{' '}
          <strong>Microsoft / therapymgmt.com</strong> account, so password
          changes, MFA, and account recovery all happen inside Microsoft (or via
          your IT helpdesk) — not here.
        </p>
        <p className="text-sm text-[#5C635A]">
          If you need to reset your Microsoft password, visit{' '}
          <a href="https://passwordreset.microsoftonline.com" target="_blank" rel="noreferrer" className="text-[#7A8B7A] underline">
            passwordreset.microsoftonline.com
          </a>
          .
        </p>
      </>
    ),
  },

  // ===== Admin-only =======================================================
  {
    id: 'admin-roles',
    title: 'Managing roles',
    audience: 'admin',
    body: () => (
      <>
        <p>Roles are assigned in two places:</p>
        <ul>
          <li>
            The email configured in the server’s <code>ADMIN_EMAIL</code> env var
            (currently <code>tplunkett@therapymgmt.com</code>) is{' '}
            <strong>always</strong> promoted to Administrator on sign-in.
          </li>
          <li>
            For everyone else, you pre-assign a role in the <strong>Users</strong> tab.
            When they sign in with Microsoft (matching by email), they inherit
            the role you set.
          </li>
        </ul>
        <p>
          To change a user’s role later, open the Users tab and pick a new value
          from the <em>Role</em> dropdown next to their row. The change is saved
          immediately. You can’t change your own role.
        </p>
      </>
    ),
  },
  {
    id: 'admin-users',
    title: 'Inviting users',
    audience: 'admin',
    body: () => (
      <>
        <p>
          TMC Surveys does not manage passwords — users sign in with their
          Microsoft / therapymgmt.com account. To grant someone access:
        </p>
        <ol>
          <li>Open the <strong>Users</strong> tab on the dashboard.</li>
          <li>Click <strong>Add User</strong>.</li>
          <li>Enter their full name and Microsoft sign-in email (must match exactly).</li>
          <li>Pick a role: <em>Creator</em> (default) or <em>Admin</em>.</li>
          <li>Save. Tell them to visit the site and click <em>Sign in with Microsoft</em>.</li>
        </ol>
        <p className="text-sm text-[#5C635A]">
          You can delete a user any time from the Users tab — they lose access on
          their next request. You cannot delete yourself.
        </p>
      </>
    ),
  },
  {
    id: 'admin-groups',
    title: 'Managing groups',
    audience: 'admin',
    body: () => (
      <>
        <p>
          Groups let you share surveys with a team without making the survey public
          or admin-restricted. Anyone in a group sees every survey tagged with that
          group on their dashboard, with read-only access (view + run reports).
        </p>
        <ol>
          <li>Open the <strong>Groups</strong> tab on the dashboard.</li>
          <li>Click <strong>New Group</strong> and enter a name (e.g., <em>Branding</em>) and an optional description.</li>
          <li>
            On the group card, pick a teammate from the <em>Add a member…</em> dropdown and click{' '}
            <strong>Add</strong>. Add as many people as you like.
          </li>
          <li>
            Tell the survey owner (or do it yourself) to open the survey in the editor and pick
            this group from the <strong>Groups &amp; Sharing</strong> dropdown. The share takes
            effect immediately.
          </li>
        </ol>
        <p>
          <strong>Renaming or deleting a group:</strong> use the pencil and trash icons on the
          group card. Deleting a group only removes the share — the surveys themselves are not
          deleted. Members lose access on their next request.
        </p>
        <p className="text-sm text-[#5C635A]">
          A user can belong to multiple groups, and a survey can carry multiple group tags —
          membership is additive.
        </p>
      </>
    ),
  },
  {
    id: 'admin-sso',
    title: 'Microsoft SSO configuration',
    audience: 'admin',
    body: () => (
      <>
        <p>
          The Microsoft sign-in flow is configured via environment variables on the server:
        </p>
        <pre className="bg-[#FDFBF7] border border-[#E5E0D8] rounded-lg p-3 text-xs overflow-x-auto">{`MICROSOFT_TENANT_ID=...
MICROSOFT_CLIENT_ID=...
MICROSOFT_CLIENT_SECRET=...
MICROSOFT_REDIRECT_URI=https://tmcsurvey.therapymgmt.com/api/auth/microsoft/callback
POST_LOGIN_REDIRECT=https://tmcsurvey.therapymgmt.com/admin`}</pre>
        <p>
          In Azure App Registration, the same redirect URI must be registered
          under <em>Authentication → Web</em>. After changing any value run{' '}
          <code>docker compose restart backend</code>.
        </p>
        <p className="text-sm text-[#5C635A]">
          If the “Sign in with Microsoft” button doesn’t appear on the login page,
          check that all four MICROSOFT_* variables are present and non-empty.
        </p>
      </>
    ),
  },
  {
    id: 'admin-smtp',
    title: 'SMTP / email configuration',
    audience: 'admin',
    body: () => (
      <>
        <p>
          Scheduled reports use Office 365 SMTP. Set these on the server:
        </p>
        <pre className="bg-[#FDFBF7] border border-[#E5E0D8] rounded-lg p-3 text-xs overflow-x-auto">{`SMTP_HOST=smtp.office365.com
SMTP_PORT=587
SMTP_USER=licensed-user@therapymgmt.com   # NOT the shared mailbox
SMTP_PASSWORD=...                         # or app password
SMTP_FROM=noreply-supportportal@therapymgmt.com`}</pre>
        <p>
          For a shared mailbox: <code>SMTP_USER</code> must be a licensed user
          with <strong>Send As</strong> permission on the shared mailbox.
          Authenticated SMTP must be enabled on that user (M365 admin → Users →
          Mail → Manage email apps).
        </p>
      </>
    ),
  },
  {
    id: 'admin-backup',
    title: 'Backup & recovery',
    audience: 'admin',
    body: () => (
      <>
        <p>
          All data lives in MongoDB inside the <code>mongo</code> container. To
          take a backup:
        </p>
        <pre className="bg-[#FDFBF7] border border-[#E5E0D8] rounded-lg p-3 text-xs overflow-x-auto">{`# Dump to a timestamped folder
docker compose exec mongo \\
  mongodump --uri="mongodb://admin:surveypass123@localhost:27017/?authSource=admin" \\
  --out=/data/db/backup-$(date +%F)`}</pre>
        <p>
          Then copy the folder off the host (rsync / scp). Restore with{' '}
          <code>mongorestore</code> against an empty database.
        </p>
        <p className="text-sm text-[#5C635A]">
          Uploaded logos live in the <code>uploads</code> volume. Include the
          volume in your backup if you want to restore exact branding.
        </p>
      </>
    ),
  },
  {
    id: 'admin-deploy',
    title: 'Deployment cheat-sheet',
    audience: 'admin',
    body: () => (
      <>
        <pre className="bg-[#FDFBF7] border border-[#E5E0D8] rounded-lg p-3 text-xs overflow-x-auto">{`# Pull updates and rebuild
cd /opt/survey-platform
git pull origin main
docker compose up -d --build
docker compose logs -f backend

# Restart only the backend (env-only change)
docker compose restart backend

# View running containers
docker compose ps

# Open a Mongo shell
docker compose exec mongo \\
  mongo -u admin -p surveypass123 --authenticationDatabase admin survey_platform`}</pre>
      </>
    ),
  },
  {
    id: 'admin-insights-exec',
    title: 'Executive summary (Insights tab)',
    audience: 'admin',
    body: () => (
      <>
        <p>
          The <strong>Insights</strong> tab on the admin dashboard is your one-page
          system health check — what's happening across every survey, every creator,
          and every group, refreshable on demand.
        </p>
        <p>It surfaces four kinds of information:</p>
        <ol>
          <li>
            <strong>Top-line counts</strong> — surveys (with active/inactive/shared
            split), users (admin vs creator + new signups), responses (with 24 h /
            7 d / 30 d / 60 d / 90 d windows), and the org-wide completion rate.
          </li>
          <li>
            <strong>Trends</strong> — a 30-day response line chart, a day-of-week bar
            chart, and the top 5 surveys by traffic.
          </li>
          <li>
            <strong>Ownership &amp; sharing</strong> — a full <em>Surveys &amp;
            responses by creator</em> table (every owner, every count), plus a{' '}
            <em>Group usage</em> table that shows members, tagged surveys, and
            responses driven by each group. Empty / unused groups are called out
            so you can clean them up.
          </li>
          <li>
            <strong>Action lists</strong> — dormant active surveys (no responses in
            30 days), zero-response surveys, AI usage stats, and an Operational
            Health card (uploads on disk, file count, scheduled reports, avg.
            questions per survey, new users).
          </li>
        </ol>
        <p className="text-sm text-[#5C635A]">
          Everything pulls live from <code>GET /api/admin/insights</code> and is
          admin-only — Creators don't see this tab at all. The numbers are
          deterministic (counts and sums), so they're safe to drop into a
          screenshot for a leadership update.
        </p>
        <p className="text-sm text-[#5C635A]">
          Hit <strong>Refresh</strong> at the top right to re-pull. There's no
          background caching: every load returns fresh aggregates. The
          <strong>Download PDF</strong> button next to <em>Refresh</em> renders this whole
          summary into a printable executive briefing (cover, headline numbers, response
          volume, top surveys, full creator ledger, group usage, action items, operational
          health) — perfect to drop into a board update without screenshot-stitching.
        </p>
      </>
    ),
  },
  {
    id: 'admin-ai-cost',
    title: 'AI usage & costs',
    audience: 'admin',
    body: () => (
      <>
        <p>
          AI Insights uses Anthropic <strong>Claude Sonnet 4.5</strong> through
          the Emergent Universal Key (<code>EMERGENT_LLM_KEY</code>).
          Every call deducts credits from your key’s balance.
        </p>
        <table className="w-full text-left mt-3 mb-4 border border-[#E5E0D8] rounded-lg overflow-hidden">
          <thead className="bg-[#F4F1EA] text-sm text-[#5C635A]">
            <tr>
              <th className="px-4 py-2">Scenario</th>
              <th className="px-4 py-2">Approx. cost / call</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">1–3 surveys, ≤ 100 responses</td><td className="px-4 py-2">$0.02 – $0.05</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">5–10 surveys, 500+ responses with free text</td><td className="px-4 py-2">$0.10 – $0.25</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">Maximum (100 surveys, capped prompt)</td><td className="px-4 py-2">~$0.50</td></tr>
          </tbody>
        </table>
        <p>Built-in cost protection (already enforced server-side):</p>
        <ul>
          <li>Free-text capped at <strong>20 most-recent answers per question, 400 chars each</strong>.</li>
          <li>Total prompt hard-capped at <strong>60,000 characters</strong> (~15k input tokens).</li>
          <li><strong>60-second timeout</strong> so a stuck call never bills indefinitely.</li>
          <li>On any failure, the report still ships <em>without</em> the AI section — the regular content is unaffected.</li>
        </ul>
        <p>
          To monitor & top up balance go to{' '}
          <a href="https://app.emergent.sh" target="_blank" rel="noreferrer" className="text-[#7A8B7A] underline">
            app.emergent.sh
          </a> → Profile → Universal Key. Enable <em>Auto top-up</em> if you don’t
          want a depleted balance to surprise you.
        </p>
        <p className="text-sm text-[#5C635A]">
          The <strong>Insights</strong> tab on the dashboard shows how many
          summaries have been generated in the last 7 / 30 days and who last
          triggered one, so you can correlate usage with the balance graph in Emergent.
        </p>
      </>
    ),
  },
  {
    id: 'admin-tech-why',
    title: 'Tech Reference · Why this app was built',
    audience: 'admin',
    body: () => (
      <>
        <p>
          TMC Surveys began as a single static HTML page that posted patient
          feedback to a Microsoft Power Automate flow. That worked for one form,
          but as the program grew, three pressure points emerged:
        </p>
        <ol>
          <li>
            <strong>Vendor lock-in & cost.</strong> Power Automate billing
            scales with flow runs, and the data lived in a SharePoint list we
            couldn’t freely query.
          </li>
          <li>
            <strong>Reporting limitations.</strong> Cross-survey trend
            analysis required exporting CSVs and stitching them in Excel by hand.
          </li>
          <li>
            <strong>Branding & per-program theming.</strong> Each program wanted
            its own logo, colors, and tone. A single shared form couldn’t do
            that.
          </li>
        </ol>
        <p>
          The goal: a <strong>self-hosted, modular, multi-survey platform</strong> the
          clinic owns end-to-end, deployable behind our own domain with our own
          identity provider (Microsoft Entra ID), our own database, and our own
          backups.
        </p>
        <p>
          Secondary goals that emerged as the system matured:
        </p>
        <ul>
          <li>Role-based access (Creator vs Administrator)</li>
          <li>QR-code distribution for waiting-room flyers</li>
          <li>Scheduled email reports without manual exports</li>
          <li>AI-written executive summaries for leadership readouts</li>
          <li>A clean theming system so each program’s survey looks distinct</li>
        </ul>
      </>
    ),
  },
  {
    id: 'admin-tech-architecture',
    title: 'Tech Reference · Architecture',
    audience: 'admin',
    body: () => (
      <>
        <p>
          The system is a three-container Docker stack behind a host-level
          nginx that terminates TLS:
        </p>
        <pre className="bg-[#FDFBF7] border border-[#E5E0D8] rounded-lg p-3 text-xs overflow-x-auto">{`Browser
   │  HTTPS
   ▼
Host nginx (Let's Encrypt)         /etc/nginx/sites-available/tmcsurvey
   │  proxy_pass http://127.0.0.1:3000
   ▼
[survey_frontend container]  port 80
  internal nginx
   ├─ /         → React SPA (static build, Tailwind + Recharts)
   ├─ /api/*    → proxy_pass http://backend:8001
   └─ /uploads/* → proxy_pass http://backend:8001
   │
   ▼
[survey_backend container]   port 8001
  FastAPI (uvicorn, single worker)
   ├─ JWT auth (email/pw fallback)
   ├─ MSAL Confidential Client (Azure SSO, auth-code flow + PKCE)
   ├─ RBAC: creator → own surveys, admin → all
   ├─ Survey/Submission/User CRUD
   ├─ Analytics + multi-survey report generation
   │     CSV / Excel via pandas + openpyxl
   │     PDF  via reportlab
   ├─ AI summaries via emergentintegrations → Claude Sonnet 4.5
   ├─ Scheduled email reports via APScheduler + aiosmtplib (SMTP O365)
   └─ Persistent uploads in the host volume ./uploads
   │
   ▼
[survey_mongodb container]   Mongo 4.4
  Collections: users, surveys, submissions, ai_usage_logs
  Volume:      mongodb_data  (host-mounted)`}</pre>
        <p>
          <strong>Why single-worker uvicorn?</strong> The MSAL auth-code flow
          state is held in process memory. Multi-worker would lose state across
          requests. Roadmap item: externalize to Redis to allow horizontal scale.
        </p>
        <p>
          <strong>Why MongoDB?</strong> Survey schemas are user-defined and vary
          per survey (different question lists). A document store fits naturally;
          Postgres + JSONB would also work but require schema migrations for any
          shape change.
        </p>
      </>
    ),
  },
  {
    id: 'admin-tech-data-model',
    title: 'Tech Reference · Data model',
    audience: 'admin',
    body: () => (
      <>
        <p>Four MongoDB collections:</p>
        <pre className="bg-[#FDFBF7] border border-[#E5E0D8] rounded-lg p-3 text-xs overflow-x-auto">{`users
  _id            ObjectId
  email          string  (unique, indexed)
  name           string
  role           'admin' | 'creator'
  ms_oid         string  (Microsoft object id, present for SSO users)
  auth_provider  'local' | 'microsoft' | 'sso_invited'
  password_hash  bcrypt  (random for sso_invited)
  created_at     datetime UTC
  last_login     datetime UTC

surveys
  _id              ObjectId
  name             string
  description      string
  active           bool
  questions        [ { id, type, question, required, options? } ]
                     type ∈ {'star','radio','text','textarea'}
  theme            { logo_url, primary_color, secondary_color,
                     background_color, text_color, preset, font_family,
                     background_style, card_style }
  report_schedule  { enabled, frequency, recipients, day_of_week,
                     day_of_month, hour }
  creator_id       string   (users._id as string — back-fills on startup)
  creator_email    string   (denormalized for fast display)
  created_at       datetime UTC

submissions
  _id           ObjectId
  survey_id     string (surveys._id as string)
  responses     { [question_id]: any }   # star=int, radio=value, text=string
  submitted_at  datetime UTC   (indexed for time-range queries)

ai_usage_logs
  _id              ObjectId
  user_id          string
  user_email       string
  user_role        'admin' | 'creator'
  survey_ids       [string]
  surveys_count    int
  responses_count  int
  model            'claude-sonnet-4-5-20250929'
  summary_chars    int
  embedded_in_pdf  bool   (true when AI was included in a PDF download)
  created_at       datetime UTC`}</pre>
      </>
    ),
  },
  {
    id: 'admin-tech-auth',
    title: 'Tech Reference · Authentication & RBAC',
    audience: 'admin',
    body: () => (
      <>
        <p>Two paths into the app, one authorization layer:</p>
        <ol>
          <li>
            <strong>Microsoft SSO</strong> — OAuth2 Authorization Code Flow with
            PKCE via <code>msal.ConfidentialClientApplication</code>.
            <ul>
              <li><code>GET /api/auth/microsoft/login</code> initiates the flow and 307-redirects to <code>login.microsoftonline.com/{'{tenant}'}/oauth2/v2.0/authorize</code></li>
              <li><code>GET /api/auth/microsoft/callback</code> exchanges the authorization code for an ID token, validates the <code>oid</code> + <code>email</code> claims, upserts the user, and sets <code>access_token</code> + <code>refresh_token</code> httpOnly cookies (Secure, SameSite=Lax)</li>
              <li>Role assignment: <code>ADMIN_EMAIL</code> auto → admin; everyone else inherits the role pre-set by an administrator in the Users tab (default <code>creator</code> if no invite row exists)</li>
            </ul>
          </li>
          <li>
            <strong>Email + password fallback</strong> — bcrypt-hashed, kept so
            an administrator can log in for break-glass scenarios when Azure is
            unreachable. JWT signed with <code>JWT_SECRET</code>.
          </li>
        </ol>
        <p>
          <strong>RBAC is enforced per request.</strong> Every survey /
          analytics / report endpoint calls{' '}
          <code>can_access_survey(user, survey)</code>, which returns true if
          the user is admin OR <code>survey.creator_id == user.id</code>. The{' '}
          <code>/api/admin/*</code> endpoints check{' '}
          <code>user.role == 'admin'</code> as their first line.
        </p>
        <p>
          Frontend hides admin-only tabs (Users, Insights) and admin-only manual
          sections by reading <code>user.role</code>, but the server is the
          source of truth — a creator hitting <code>/api/admin/insights</code>{' '}
          gets a 403 regardless of what the UI shows.
        </p>
      </>
    ),
  },
  {
    id: 'admin-tech-reports-pipeline',
    title: 'Tech Reference · Reports & AI pipeline',
    audience: 'admin',
    body: () => (
      <>
        <p>The report endpoint shares one data-loading helper:</p>
        <pre className="bg-[#FDFBF7] border border-[#E5E0D8] rounded-lg p-3 text-xs overflow-x-auto">{`_load_surveys_with_access(survey_ids, user, date_from, date_to)
  ├─ validate ids (<=100, valid ObjectId)
  ├─ Mongo lookup → list[survey]
  ├─ RBAC check per survey
  ├─ ISO date parsing (date_to → end-of-day)
  └─ fetch submissions per survey in the window
                        │
   ┌────────────────────┼──────────────────────┐
   ▼                    ▼                      ▼
build_csv()         build_excel()          build_pdf(ai_summary?)
 (pandas long-      (one xlsx sheet/       (reportlab — cover +
  format, one row    survey + Summary       optional Executive
  per Q/response)    sheet)                 Summary page +
                                            per-survey detail)

If include_ai_summary=true (PDF only) we also call:

ai_insights.summarize_surveys(surveys, submissions_by_survey, ...)
  ├─ build prompt: aggregated stats + free-text samples
  ├─ LlmChat(EMERGENT_LLM_KEY)
  │     .with_model("anthropic", "claude-sonnet-4-5-20250929")
  ├─ await send_message(UserMessage(text=prompt))  # 60s timeout
  └─ on any exception → return None (report still ships)

Each successful summary writes one row to ai_usage_logs for
the Insights tab.`}</pre>
        <p>
          The same helper feeds <code>POST /api/analytics/ai-summary</code>{' '}
          (on-screen panel) and <code>POST /api/analytics/report</code>{' '}
          (PDF/Excel/CSV download), so RBAC and date-window behavior stay
          identical across surfaces.
        </p>
      </>
    ),
  },
  {
    id: 'admin-tech-themes',
    title: 'Tech Reference · Theme system',
    audience: 'admin',
    body: () => (
      <>
        <p>
          Themes are plain objects persisted on the survey document. The single
          source of truth is{' '}
          <code>frontend/src/lib/themes.js</code>, which exports presets and
          two render helpers:
        </p>
        <ul>
          <li>
            <code>backgroundCss(theme)</code> returns inline CSS for the page
            background — pure CSS / inline SVG, no image fetches. Supports{' '}
            <em>solid, gradient, dots, grid, waves, soft-blobs</em>.
          </li>
          <li>
            <code>cardCss(theme)</code> returns inline CSS for the survey card
            — <em>flat, elevated, glass</em> (backdrop-blur),{' '}
            <em>bordered</em>.
          </li>
        </ul>
        <p>
          The same helpers are used by the editor’s live preview and the public
          survey page, so what creators see is byte-for-byte what respondents
          see. Custom hex colors mark the theme as <code>preset: 'custom'</code>{' '}
          so the preset highlight no longer shows.
        </p>
        <p>
          Fonts are loaded once in <code>public/index.html</code> from Google
          Fonts (Outfit, Inter, Playfair Display, Space Grotesk) — adding a new
          font is a one-line addition there plus a row in <code>FONT_OPTIONS</code>.
        </p>
      </>
    ),
  },
  {
    id: 'admin-tech-stack',
    title: 'Tech Reference · Stack & libraries',
    audience: 'admin',
    body: () => (
      <>
        <table className="w-full text-left mt-3 mb-4 border border-[#E5E0D8] rounded-lg overflow-hidden text-xs">
          <thead className="bg-[#F4F1EA] text-sm text-[#5C635A]">
            <tr>
              <th className="px-4 py-2">Layer</th>
              <th className="px-4 py-2">Tech / Library</th>
              <th className="px-4 py-2">Why</th>
            </tr>
          </thead>
          <tbody className="text-[12px]">
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">UI</td><td className="px-4 py-2">React 18 · Tailwind 3 · shadcn/ui</td><td className="px-4 py-2">Component library + utility CSS — fast iteration</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">Charts</td><td className="px-4 py-2">Recharts</td><td className="px-4 py-2">Composable React charts, theming-friendly</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">Icons</td><td className="px-4 py-2">lucide-react</td><td className="px-4 py-2">Consistent line-icon set</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">API</td><td className="px-4 py-2">FastAPI + uvicorn</td><td className="px-4 py-2">Async, Pydantic models, OpenAPI for free</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">DB driver</td><td className="px-4 py-2">motor (async pymongo)</td><td className="px-4 py-2">Non-blocking Mongo access in the FastAPI event loop</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">Auth</td><td className="px-4 py-2">PyJWT 2 · bcrypt · msal 1.36</td><td className="px-4 py-2">SSO + local fallback in the same dependency tree</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">Reports</td><td className="px-4 py-2">pandas · openpyxl · reportlab</td><td className="px-4 py-2">CSV / Excel / PDF respectively</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">QR codes</td><td className="px-4 py-2">qrcode[pil]</td><td className="px-4 py-2">PNG output, error correction</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">LLM</td><td className="px-4 py-2">emergentintegrations → Claude Sonnet 4.5</td><td className="px-4 py-2">Single Universal Key for AI insights</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">Scheduler</td><td className="px-4 py-2">APScheduler · aiosmtplib</td><td className="px-4 py-2">Scheduled email reports without external cron</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">Container</td><td className="px-4 py-2">Docker · docker compose v2</td><td className="px-4 py-2">Single-host reproducible deploy</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">Edge</td><td className="px-4 py-2">nginx (host + frontend container)</td><td className="px-4 py-2">TLS at host, SPA + /api routing inside the stack</td></tr>
          </tbody>
        </table>
      </>
    ),
  },
  {
    id: 'admin-tech-endpoints',
    title: 'Tech Reference · API endpoint map',
    audience: 'admin',
    body: () => (
      <>
        <pre className="bg-[#FDFBF7] border border-[#E5E0D8] rounded-lg p-3 text-xs overflow-x-auto">{`Auth
  POST  /api/auth/login                 email + password
  GET   /api/auth/me                    current user (cookie)
  POST  /api/auth/logout                clear cookies
  GET   /api/auth/config                {microsoft_sso_enabled: bool}
  GET   /api/auth/microsoft/login       redirect → Microsoft
  GET   /api/auth/microsoft/callback    code exchange, sets cookies

Surveys (RBAC: creator → own, admin → all)
  GET    /api/surveys                   list accessible surveys
  POST   /api/surveys                   create
  GET    /api/surveys/{id}              one survey
  PUT    /api/surveys/{id}              edit
  DELETE /api/surveys/{id}              delete
  GET    /api/surveys/{id}/qr           PNG QR of public URL
  GET    /api/surveys/public/{id}       public render (no auth)
  POST   /api/surveys/{id}/submit       submit response (no auth)

Analytics (same RBAC)
  GET   /api/analytics/{id}             charts data
  GET   /api/analytics/{id}/responses   paginated raw responses
  GET   /api/analytics/{id}/export/csv
  GET   /api/analytics/{id}/export/excel
  GET   /api/analytics/{id}/export/pdf
  POST  /api/analytics/report           multi-survey CSV/Excel/PDF
  POST  /api/analytics/ai-summary       Claude exec-summary (on-screen)

Admin (admin role required)
  GET    /api/admin/users               list users
  POST   /api/admin/users               invite (no password = SSO-only)
  PATCH  /api/admin/users/{id}/role     promote/demote
  DELETE /api/admin/users/{id}          remove
  GET    /api/admin/insights            org-wide executive summary

Misc
  POST  /api/upload                     logo upload (auth required)
  GET   /uploads/<file>                 static-served from backend
  GET   /api/dashboard/stats            tile counts (RBAC-scoped)`}</pre>
      </>
    ),
  },
  {
    id: 'admin-tech-files',
    title: 'Tech Reference · File map',
    audience: 'admin',
    body: () => (
      <>
        <pre className="bg-[#FDFBF7] border border-[#E5E0D8] rounded-lg p-3 text-xs overflow-x-auto">{`/app/
├── backend/
│   ├── server.py            All FastAPI routes (~1400 lines)
│   ├── reports.py           CSV / Excel / PDF builders
│   ├── ai_insights.py       Claude-powered summarize_surveys()
│   ├── requirements.txt
│   ├── Dockerfile
│   └── tests/               pytest suite (RBAC + reports + SSO)
├── frontend/
│   ├── public/
│   │   ├── index.html       Google Fonts + favicon
│   │   └── tmc-survey-logo.png
│   ├── src/
│   │   ├── App.js
│   │   ├── contexts/
│   │   │   └── AuthContext.js
│   │   ├── components/
│   │   │   ├── ProtectedRoute.js
│   │   │   ├── Logo.js
│   │   │   ├── ThemePicker.js     6 presets + live preview
│   │   │   ├── ReportsTab.js      Multi-survey reports + AI panel
│   │   │   ├── InsightsTab.js     Admin org-wide dashboard
│   │   │   └── ui/                shadcn primitives
│   │   ├── lib/
│   │   │   └── themes.js          backgroundCss / cardCss helpers
│   │   └── pages/
│   │       ├── Login.js
│   │       ├── AdminDashboard.js
│   │       ├── SurveyEditor.js
│   │       ├── SurveyAnalytics.js
│   │       ├── PublicSurvey.js
│   │       └── Help.js            This manual
│   ├── nginx.conf           SPA + /api proxy inside the container
│   └── Dockerfile
├── deploy/
│   └── nginx-host.conf.example
├── docker-compose.yml
├── .env.example
└── memory/PRD.md            Living product spec`}</pre>
      </>
    ),
  },
  {
    id: 'admin-tech-troubleshoot',
    title: 'Tech Reference · Troubleshooting playbook',
    audience: 'admin',
    body: () => (
      <>
        <table className="w-full text-left mt-3 mb-4 border border-[#E5E0D8] rounded-lg overflow-hidden text-xs">
          <thead className="bg-[#F4F1EA] text-sm text-[#5C635A]">
            <tr>
              <th className="px-4 py-2">Symptom</th>
              <th className="px-4 py-2">First check</th>
            </tr>
          </thead>
          <tbody className="text-[12px] leading-relaxed">
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">502 from nginx</td><td className="px-4 py-2"><code>docker compose ps</code> — likely backend crashed. Tail logs: <code>docker compose logs --tail=100 backend</code>.</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">Mongo “Authentication failed”</td><td className="px-4 py-2"><code>MONGO_PASSWORD</code> in .env diverged from what the volume was initialized with. Either reset password via <code>db.changeUserPassword()</code> or <code>docker compose down -v</code> (destroys data) and rebuild.</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">SSO 400 “client is public”</td><td className="px-4 py-2">Azure App Registration platform must be <strong>Web</strong>, not Single-page application. Public client flow toggled <em>off</em>.</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">SSO 50011 redirect mismatch</td><td className="px-4 py-2">URI in Azure must equal <code>MICROSOFT_REDIRECT_URI</code> in .env exactly (no trailing slash).</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">AI summary 503</td><td className="px-4 py-2"><code>EMERGENT_LLM_KEY</code> missing OR Universal Key balance is 0. Top up at app.emergent.sh.</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">Logo upload fails silently</td><td className="px-4 py-2">File &gt; 5 MB, unsupported extension, or axios <code>Content-Type</code> override (now removed). Inspect Network → Response in browser devtools.</td></tr>
            <tr className="border-t border-[#E5E0D8]"><td className="px-4 py-2">Scheduled email not sent</td><td className="px-4 py-2">Licensed user lacks Send As on the shared mailbox, Authenticated SMTP disabled, or app password expired. Backend log shows <code>Failed to send report</code>.</td></tr>
          </tbody>
        </table>
      </>
    ),
  },
];

// FAQ -----------------------------------------------------------------------

const FAQ_ITEMS = [
  // Universal
  {
    q: 'Why don’t I see surveys someone else made?',
    audience: 'creator',
    a: () => (
      <p>
        By default, Creators only see surveys they created. If a colleague wants you to see
        their survey, they (or an admin) can tag the survey with a <strong>group</strong> you
        belong to. The survey will appear on your dashboard with a <em>Shared</em> badge and
        you can run reports from it (read-only). Don’t belong to the right group? Ask an
        administrator to add you.
      </p>
    ),
  },
  {
    q: 'How do I share a survey with my team?',
    audience: 'all',
    a: () => (
      <p>
        Tag the survey with a <strong>group</strong>. Open the survey in the editor → scroll to{' '}
        <em>Groups &amp; Sharing</em> → pick one or more groups from the dropdown. Everyone in
        those groups will see the survey on their dashboard. Only admins can create groups —
        ask one if the group you need doesn’t exist yet.
      </p>
    ),
  },
  {
    q: 'Can a group member edit or delete my survey?',
    audience: 'all',
    a: () => (
      <p>
        No. Group sharing is <strong>read-only</strong>. Members can view the survey, see
        analytics, and run reports — but only the survey’s owner and administrators can edit,
        retag, or delete it.
      </p>
    ),
  },
  {
    q: 'How do I notify group members when I share a survey with them?',
    audience: 'all',
    a: () => (
      <p>
        Tick <strong>“Email members of newly-added groups when I save”</strong> in the
        Groups &amp; Sharing section of the editor. We send a one-time heads-up email to
        everyone in any group you just added, with a button that takes them to their dashboard.
        Members of groups that were already tagged are <em>not</em> re-notified — so you can
        re-save the survey safely. Email delivery uses the SMTP credentials configured by your
        admin; if SMTP isn’t configured the notification is silently skipped.
      </p>
    ),
  },
  {
    q: 'Where did the “Users” tab go?',
    audience: 'creator',
    a: () => (
      <p>User management is admin-only, so you won’t see that tab. It’s not missing — it’s just not for your role.</p>
    ),
  },
  {
    q: 'Can I edit a question after responses have come in?',
    audience: 'all',
    a: () => (
      <p>
        Yes, but only do this for typos or clarifications. Renaming a question
        does <strong>not</strong> rewrite older responses, so the export will
        keep the historical column name from when the response was submitted.
      </p>
    ),
  },
  {
    q: 'How do respondents take a survey?',
    audience: 'all',
    a: () => (
      <p>
        They open the public URL (or scan its QR code) — no account, no login.
        They fill out the form and tap Submit.
      </p>
    ),
  },
  {
    q: 'Can I duplicate an existing survey to start a new one?',
    audience: 'all',
    a: () => (
      <p>
        Yes. Click the <strong>Duplicate</strong> icon (looks like two stacked pages) on any
        survey card on your dashboard. We'll prompt you for a new name (must be different from
        the original), then drop you into the editor for the freshly-created copy. Questions,
        description, and theme carry over so you can tweak from a known-good starting point.
        Responses, group tags, and the email-report schedule do <em>not</em> carry over — the
        copy is yours from scratch, and the original is untouched. You can duplicate any survey
        you can see, including ones shared with you through a group.
      </p>
    ),
  },
  {
    q: 'Where did my inactive surveys go?',
    audience: 'all',
    a: () => (
      <p>
        By default the dashboard <strong>hides inactive surveys</strong> so the list stays
        focused on what's live. Tick the <strong>"Show inactive"</strong> checkbox next to the
        survey list heading to bring them back. Your choice is remembered per browser, and a
        small "<em>X inactive survey(s) hidden</em>" hint shows up at the top of the list any
        time something is filtered out.
      </p>
    ),
  },
  {
    q: 'Can I require certain questions?',
    audience: 'all',
    a: () => (
      <p>
        Yes — toggle <em>Required</em> on the question in the editor. Required questions block
        submission until answered. For "Select All That Apply", required means at least one
        option must be picked.
      </p>
    ),
  },
  {
    q: 'How do "Select All That Apply" questions show up in CSV / Excel exports?',
    audience: 'all',
    a: () => (
      <p>
        Both. For each multi-select question we add <strong>one column per option</strong>
        (with <code>1</code>/<code>0</code> per row — perfect for pivot tables) <em>plus</em>{' '}
        a <strong>single joined column</strong> labelled "<em>{'Question (selected)'}</em>"
        with the picked options separated by <code>; </code>. PDF reports show the count
        each option was picked, along with the average number of options selected per
        response.
      </p>
    ),
  },
  {
    q: 'How long are responses retained?',
    audience: 'all',
    a: () => (
      <p>
        Indefinitely. There is no automatic purge. If you delete a survey, its
        responses are deleted with it.
      </p>
    ),
  },
  {
    q: 'I deleted a survey by mistake — can I recover it?',
    audience: 'all',
    a: (role) => (
      <p>
        The UI does not undo a delete.{' '}
        {role === 'admin'
          ? 'Restore from your most recent Mongo backup (see the “Backup & recovery” section).'
          : 'Contact your administrator — they can restore from backup.'}
      </p>
    ),
  },
  {
    q: 'Can I download all responses to Excel?',
    audience: 'all',
    a: () => (
      <p>
        Yes. Open the survey’s analytics page and click <strong>Export Excel</strong> or{' '}
        <strong>Export CSV</strong>.
      </p>
    ),
  },
  {
    q: 'I changed branding — why does the public page still look old?',
    audience: 'all',
    a: () => (
      <p>
        Browsers aggressively cache logos. Hard-refresh the public page
        (Ctrl + Shift + R) or test it in a private window.
      </p>
    ),
  },
  {
    q: 'AI Insights returns an error or says it’s not available — what now?',
    audience: 'admin',
    a: () => (
      <p>
        Check that <code>EMERGENT_LLM_KEY</code> is set in <code>.env</code> on
        the server and the backend has been restarted. If the key is valid but
        the call still fails, the most likely cause is a depleted Universal Key
        balance — top it up at <em>app.emergent.sh → Profile → Universal Key</em>.
      </p>
    ),
  },
  {
    q: 'AI Insights returns an error or says it’s not available — what now?',
    audience: 'creator',
    a: () => (
      <p>
        Ask your administrator to check that the Universal LLM Key is configured
        and has remaining balance.
      </p>
    ),
  },
  {
    q: 'My logo upload fails with a generic error — what should I check?',
    audience: 'all',
    a: () => (
      <p>
        We only accept PNG, JPG, GIF, SVG, or WebP — and max 5 MB. If you’re
        uploading a high-resolution photo, scale it down to roughly 600 × 200
        before uploading. If the error is something else, the upload button now
        shows the actual reason (e.g. “Invalid file type”).
      </p>
    ),
  },
  {
    q: 'Can I make a survey feel less like a corporate form?',
    audience: 'all',
    a: () => (
      <p>
        Yes — open the survey editor, expand <strong>Branding &amp; Theme</strong>,
        and try the <em>Sunset</em> or <em>Cocoa</em> presets with a Glass card
        style. The Playfair Display font also softens the tone. The live preview
        shows the result before you save.
      </p>
    ),
  },

  // Admin-only
  {
    q: 'The “Sign in with Microsoft” button isn’t showing — what’s wrong?',
    audience: 'admin',
    a: () => (
      <p>
        One of the four <code>MICROSOFT_*</code> environment variables is missing
        on the server. SSH in and confirm with{' '}
        <code>docker compose exec backend env | grep MICROSOFT</code>. After
        fixing, run <code>docker compose restart backend</code>.
      </p>
    ),
  },
  {
    q: 'How do I promote a creator to admin?',
    audience: 'admin',
    a: () => (
      <p>
        Open the <strong>Users</strong> tab, find their row, and change the
        Role dropdown from <em>Creator</em> to <em>Admin</em>. The change saves
        immediately and applies on their next request.
      </p>
    ),
  },
  {
    q: 'Scheduled reports aren’t arriving — how do I debug?',
    audience: 'admin',
    a: () => (
      <p>
        Check <code>docker compose logs backend</code> for a line beginning with{' '}
        <code>Failed to send report</code>. Most common causes:
        the licensed SMTP user has Authenticated SMTP disabled, the user lacks
        Send As permission on the shared mailbox, or the password / app password
        has expired.
      </p>
    ),
  },
  {
    q: 'How do I rotate the Microsoft client secret?',
    audience: 'admin',
    a: () => (
      <p>
        In Azure → Certificates &amp; secrets, create a new secret value, paste
        it into <code>MICROSOFT_CLIENT_SECRET</code> on the server, run{' '}
        <code>docker compose restart backend</code>, verify a successful sign-in,
        then delete the old secret in Azure.
      </p>
    ),
  },
  {
    q: 'Can the server handle multiple workers (gunicorn –workers > 1)?',
    audience: 'admin',
    a: () => (
      <p>
        Not yet, because the MSAL auth-flow state is held in memory. Keep the
        backend at a single worker, or externalize the flow state to Redis
        before scaling out. The roadmap tracks this as a P2 item.
      </p>
    ),
  },
  {
    q: 'How do I take a quick database backup before an upgrade?',
    audience: 'admin',
    a: () => (
      <pre className="bg-[#FDFBF7] border border-[#E5E0D8] rounded-lg p-3 text-xs overflow-x-auto">{`docker compose exec mongo \\
  mongodump --uri="mongodb://admin:surveypass123@localhost:27017/?authSource=admin" \\
  --out=/data/db/backup-$(date +%F)`}</pre>
    ),
  },
];

// ---------- Component -----------------------------------------------------

const Help = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const role = user?.role || 'creator';
  const isAdmin = role === 'admin';

  const [activeId, setActiveId] = useState('welcome');
  const [query, setQuery] = useState('');

  const visibleSections = useMemo(
    () => MANUAL_SECTIONS.filter((s) => s.audience === 'all' || (s.audience === 'admin' && isAdmin)),
    [isAdmin]
  );

  const visibleFaq = useMemo(
    () =>
      FAQ_ITEMS.filter((f) => {
        if (f.audience === 'all') return true;
        if (f.audience === 'admin') return isAdmin;
        if (f.audience === 'creator') return !isAdmin;
        return false;
      }),
    [isAdmin]
  );

  // Filtered for search
  const q = query.trim().toLowerCase();
  const matchedSections = q
    ? visibleSections.filter((s) => s.title.toLowerCase().includes(q))
    : visibleSections;
  const matchedFaq = q
    ? visibleFaq.filter((f) => f.q.toLowerCase().includes(q))
    : visibleFaq;

  // Smooth-scroll to anchor when activeId changes
  useEffect(() => {
    const el = document.getElementById(`section-${activeId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [activeId]);

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E0D8] sticky top-0 z-10 print:hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center gap-3">
              <Logo size="sm" />
              <div className="hidden sm:block">
                <h1 className="text-lg font-medium text-[#2C302E] leading-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  TMC Surveys
                </h1>
                <p className="text-xs text-[#8E958C]">User Manual & FAQ</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => window.print()}
                className="hidden sm:flex items-center gap-2 px-3 py-2 border border-[#E5E0D8] rounded-full text-sm text-[#5C635A] hover:bg-[#F4F1EA] transition-colors"
                data-testid="help-print"
                title="Save the visible sections as a PDF using your browser's print dialog"
              >
                <Printer size={16} /> Export PDF
              </button>
              <Link
                to="/admin"
                className="flex items-center gap-2 px-4 py-2 bg-[#7A8B7A] hover:bg-[#637363] text-white rounded-full text-sm transition-colors"
                data-testid="help-back-to-dashboard"
              >
                <ArrowLeft size={16} /> Back to dashboard
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-28 self-start print:hidden">
          <div className="relative mb-4">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E958C]" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search manual & FAQ"
              className="w-full pl-9 pr-3 py-2 rounded-full border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#7A8B7A]/20 focus:border-[#7A8B7A]"
              data-testid="help-search"
            />
          </div>

          <p className="text-xs uppercase tracking-wide text-[#8E958C] mb-2 px-2">Manual</p>
          <nav className="flex flex-col mb-6" data-testid="help-nav-manual">
            {matchedSections.map((s) => (
              <button
                key={s.id}
                onClick={() => setActiveId(s.id)}
                className={`text-left text-sm px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
                  activeId === s.id
                    ? 'bg-[#7A8B7A]/10 text-[#2C302E]'
                    : 'text-[#5C635A] hover:bg-[#F4F1EA]'
                }`}
                data-testid={`help-nav-${s.id}`}
              >
                {s.audience === 'admin' && (
                  <span title="Admin only" className="text-[10px] uppercase tracking-wider bg-[#C67D63]/15 text-[#9c5340] px-1.5 py-0.5 rounded">
                    Admin
                  </span>
                )}
                <span className="flex-1">{s.title}</span>
                <ChevronRight size={14} className="opacity-50" />
              </button>
            ))}
            {matchedSections.length === 0 && (
              <p className="text-xs text-[#8E958C] px-3 py-2">No matching sections.</p>
            )}
          </nav>

          <p className="text-xs uppercase tracking-wide text-[#8E958C] mb-2 px-2">FAQ</p>
          <button
            onClick={() => setActiveId('faq')}
            className={`w-full text-left text-sm px-3 py-2 rounded-lg flex items-center gap-2 transition-colors ${
              activeId === 'faq'
                ? 'bg-[#7A8B7A]/10 text-[#2C302E]'
                : 'text-[#5C635A] hover:bg-[#F4F1EA]'
            }`}
            data-testid="help-nav-faq"
          >
            <HelpCircle size={14} />
            <span className="flex-1">Common questions</span>
            <ChevronRight size={14} className="opacity-50" />
          </button>
        </aside>

        {/* Main content */}
        <main className="bg-white border border-[#E5E0D8] rounded-2xl p-6 sm:p-10 print:border-0 print:p-0">
          <div className="flex items-center gap-2 text-xs text-[#8E958C] mb-2">
            <BookOpen size={14} />
            <span>Tailored for your role:</span>
            <span className="px-2 py-0.5 rounded-full bg-[#7A8B7A]/10 text-[#7A8B7A] uppercase tracking-wide">
              {isAdmin ? 'Administrator' : 'Creator'}
            </span>
          </div>
          <h1
            className="text-3xl sm:text-4xl font-light text-[#2C302E] mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            User Manual
          </h1>

          <div className="prose-tmc space-y-12" data-testid="help-content">
            {matchedSections.map((s) => (
              <section key={s.id} id={`section-${s.id}`} data-testid={`help-section-${s.id}`}>
                <h2
                  className="text-2xl font-medium text-[#2C302E] mb-3 flex items-center gap-2"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {s.title}
                  {s.audience === 'admin' && (
                    <span className="text-[10px] uppercase tracking-wider bg-[#C67D63]/15 text-[#9c5340] px-2 py-0.5 rounded">
                      Admin
                    </span>
                  )}
                </h2>
                <div className="text-[#2C302E] leading-relaxed text-[15px] [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-3 [&_code]:bg-[#F4F1EA] [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[13px]">
                  {s.body(role)}
                </div>
              </section>
            ))}
          </div>

          {/* FAQ */}
          <section id="section-faq" className="mt-16 pt-8 border-t border-[#E5E0D8]" data-testid="help-faq-section">
            <h2
              className="text-2xl font-medium text-[#2C302E] mb-6 flex items-center gap-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              <HelpCircle size={22} /> Frequently Asked Questions
            </h2>

            {matchedFaq.length === 0 ? (
              <p className="text-sm text-[#8E958C]">No matching questions.</p>
            ) : (
              <div className="space-y-4">
                {matchedFaq.map((f, idx) => (
                  <FaqItem key={idx} item={f} role={role} index={idx} />
                ))}
              </div>
            )}
          </section>

          <p className="mt-12 text-xs text-[#8E958C]">
            Need more help? Contact your TMC Surveys administrator.
          </p>
        </main>
      </div>
    </div>
  );
};

const FaqItem = ({ item, role, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#E5E0D8] rounded-xl overflow-hidden bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-[#F4F1EA] transition-colors"
        data-testid={`faq-item-${index}`}
        aria-expanded={open}
      >
        <span className="text-[15px] text-[#2C302E] font-medium flex items-center gap-2">
          {item.audience === 'admin' && (
            <span className="text-[10px] uppercase tracking-wider bg-[#C67D63]/15 text-[#9c5340] px-1.5 py-0.5 rounded">
              Admin
            </span>
          )}
          {item.q}
        </span>
        <ChevronRight
          size={18}
          className={`text-[#8E958C] transition-transform ${open ? 'rotate-90' : ''}`}
        />
      </button>
      {open && (
        <div
          className="px-5 pb-5 text-[#2C302E] text-[14px] leading-relaxed [&_p]:mb-2 [&_code]:bg-[#F4F1EA] [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-[13px]"
          data-testid={`faq-answer-${index}`}
        >
          {item.a(role)}
        </div>
      )}
    </div>
  );
};

export default Help;
